import React, { useEffect, useState } from 'react';
import getInstallationTotalExpense from 'services/products/calculations/getInstallationTotalExpense';
import { getSellingPriceQuantityFactor } from 'services/products/calculations/getSellingPriceForMargin';
import numericStringToFloat from 'services/numbers/numericStringToFloat';
import formatGroupedNumber from 'services/numbers/formatGroupedNumber';
import { Col, Row } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import apiInstallationPrices from 'api/installationPrices';
import CollapseIcon from 'components/layout/icons/CollapseIcon';
import formatFloatForDisplay from 'services/numbers/formatFloatForDisplay';
import getShownDifference from 'services/numbers/getShownDifference';
import getContributionMarginRatio from 'services/products/calculations/getContributionMarginRatio';
import getSellingPricePerUnit from 'services/products/calculations/getSellingPricePerUnit';
import getExpensePerUnit from 'services/products/calculations/getExpensePerUnit';
import getExpenseTotal from 'services/products/calculations/getExpenseTotal';
import getFormStateHandler from 'services/forms/getFormStateHandler';
import getSellingPriceTotal from 'services/products/calculations/getSellingPriceTotal';
import NumberWithDropdownInputField from 'components/layout/forms/NumberWithDropdownInputField';
import NumberWithUnitInputField from 'components/layout/forms/NumberWithUnitInputField';
import SearchDropdown from 'components/layout/forms/SearchDropdown';
import SelectInputFieldControlled from 'components/layout/forms/SelectInputFieldControlled';
import TextInputFieldNoLabel from 'components/layout/forms/TextInputFieldNoLabel';
import translations from 'services/translations/translations';
import TrashIcon from 'components/layout/icons/TrashIcon';
import { fetchUnits } from 'store/products/actions';

import {
  MAX_QUANTITY_DIGITS,
  MAX_SOCIAL_SECURITY_COST_PERCENTAGE_DIGITS,
} from 'services/offers/inputLimits';
import { useCurrency } from 'services/offers/currency';
import { useCategoryChoices } from 'services/offers/offerChoices';
import {
  withQuantityKeepingPrice,
  withSellingPriceAsSum,
} from '../givenSellingPrice';
import { priceForTargetMargin, withTargetMargin } from '../targetMargin';
import getMarginRatioClassName from '../getMarginRatioClassName';
import PositionUnitPrice from '../PositionUnitPrice';
import useMarginInputs from '../useMarginInputs';
import getInstallationCategoryOptions from '../../../../services/getInstallationCategoryOptions';
import getSellingPriceUnitOptions, {
  getSumUnitOptions,
} from '../../../../services/getSellingPriceUnitOptions';
import getUnitOptions from '../../../../services/getUnitOptions';
import IInstallation from '../../IInstallation';
import InstallationDetails from './InstallationDetails';

interface ILabelInstallationPrice {
  category: string;
  installationPrice: number;
  installationPriceUnit: string;
  label: string;
  value: string;
}

const Installation = ({
  locked,
  installation,
  openedInstallation,
  quantity,
  removeInstallation,
  setInstallation,
  setOpenedInstallation,
  targetMarginPercent,
  type,
}: {
  locked: boolean;
  installation: IInstallation;
  openedInstallation: null | number;
  quantity: number;
  removeInstallation: (installation: IInstallation) => void;
  setInstallation: (installation: IInstallation) => void;
  setOpenedInstallation: (key: null | number) => void;
  // The target margin % of the window for the rows following it
  targetMarginPercent: string;
  type: string;
}): JSX.Element => {
  // A new row prices itself for its target margin until its price is set
  const setPricedInstallation = (updated: IInstallation) =>
    setInstallation(
      withTargetMargin(
        installation,
        updated,
        getInstallationTotalExpense,
        targetMarginPercent
      )
    );

  // The price is reset (not set by the user): a row following the target margin keeps
  // following it and is priced again
  const setInstallationWithResetPrice = (updated: IInstallation) =>
    setInstallation(
      priceForTargetMargin(
        updated,
        getInstallationTotalExpense,
        targetMarginPercent
      )
    );

  const { t } = useTranslation([translations.products]);
  const categoryChoices = useCategoryChoices();
  const currency = useCurrency();

  const offerIsLocked = () => locked;

  const dispatch = useDispatch();
  const units = useSelector((state: any) => state.products.units);
  const [
    firstCollapseCustomInstallationIsSet,
    setFirstCollapseCustomInstallationIsSet,
  ] = useState(false);

  const collapsed = openedInstallation === installation.key;

  const toggleCollapse = () => {
    if (
      !firstCollapseCustomInstallationIsSet &&
      installation.installation == null
    ) {
      setFirstCollapseCustomInstallationIsSet(true);
      setInstallationWithResetPrice({
        ...installation,
        installation: null,
        isCustomInstallation: true,
        sellingPrice: '',
        sellingPriceUnit: '',
      });
    }
    setOpenedInstallation(
      openedInstallation === installation.key ? null : installation.key
    );
  };

  const handleInstallationPriceChange = (
    installationPrice: ILabelInstallationPrice | null
  ) => {
    if (installationPrice === null) {
      setInstallationWithResetPrice({
        ...installation,
        installation: null,
        sellingPrice: '',
        sellingPriceUnit: '',
      });

      return;
    }

    setPricedInstallation({
      ...installation,
      category: installationPrice.category,
      installation: {
        label: installationPrice.label,
        value: installationPrice.value,
      },
      installationPrice: formatFloatForDisplay(
        installationPrice.installationPrice
      ),
      installationPriceUnit: installationPrice.installationPriceUnit,
      quantityUnit: installationPrice.installationPriceUnit,
    });
  };

  const installationCategories = useSelector(
    (state: any) => state.products.installationCategories
  );

  useEffect(() => {
    dispatch(fetchUnits());
  }, []);

  useEffect(() => {
    const installationPriceUnit =
      installation.installationPriceUnit === '' ||
      installation.installationPriceUnit === installation.quantityUnit
        ? installation.installationPriceUnit
        : installation.quantityUnit;

    const sellingPriceUnit =
      installation.sellingPriceUnit === '' ||
      installation.sellingPriceUnit === installation.quantityUnit
        ? installation.sellingPriceUnit
        : installation.quantityUnit;

    const changed =
      installation.installationPriceUnit !== installationPriceUnit ||
      installation.sellingPriceUnit !== sellingPriceUnit;

    if (!changed) {
      return;
    }

    setPricedInstallation({
      ...installation,
      installationPriceUnit,
      sellingPriceUnit,
    });
  }, [installation]);

  const loadInstallationPrices = async (
    inputValue: string
  ): Promise<ILabelInstallationPrice[]> => {
    const list = await apiInstallationPrices.get({
      limit: 100,
      offset: 0,
      search: inputValue,
      type,
    });

    return list.map((installationPrice) => ({
      category: installationPrice.category,
      installationPrice: installationPrice.sellingPrice,
      installationPriceUnit: installationPrice.sellingPriceUnit,
      label: installationPrice.name,
      value: installationPrice.id.toString(),
    }));
  };

  const handleOtherFields = getFormStateHandler(
    installation,
    setPricedInstallation
  );

  // A given selling price is the price for one unit of the position, as on the material
  // rows (see givenSellingPrice): a typed price is a sum, and a price per unit keeps its
  // price for a unit of the position when the quantity changes
  const handleFormState = (event: any) => {
    if (event.target.name === 'sellingPrice') {
      setPricedInstallation({
        ...installation,
        sellingPrice: event.target.value,
        sellingPriceUnit: '',
      });
      return;
    }

    if (event.target.name === 'quantity') {
      setPricedInstallation(
        withQuantityKeepingPrice(installation, event.target.value)
      );
      return;
    }

    handleOtherFields(event);
  };

  const sellingPriceUnitOptions = getSellingPriceUnitOptions(
    installation.quantityUnit,
    installation.key.toString(),
    t,
    currency
  );

  const contributionMarginRatio = getContributionMarginRatio(
    [installation],
    []
  );

  const expenseForUnit = getExpensePerUnit([installation], []);
  const expenseTotal = getExpenseTotal([installation], [], quantity);

  // The margins shown: the shown selling price minus the shown expense, so they add up
  const contributionMarginForUnit = getShownDifference(
    getSellingPricePerUnit([installation], []),
    expenseForUnit
  );
  const contributionMarginTotal = getShownDifference(
    getSellingPriceTotal([installation], [], quantity),
    expenseTotal
  );

  const marginRatioClassName = getMarginRatioClassName(
    contributionMarginRatio,
    installation.sellingPrice
  );

  const marginInputs = useMarginInputs({
    contributionMargin: contributionMarginForUnit,
    contributionMarginRatio,
    expense: expenseForUnit,
    quantityFactor: getSellingPriceQuantityFactor(
      installation.sellingPriceUnit,
      numericStringToFloat(installation.quantity)
    ),
    // A price set through a margin is given too: the sum it makes for a unit of the position
    setSellingPrice: (sellingPrice: string) =>
      setPricedInstallation(
        withSellingPriceAsSum({ ...installation, sellingPrice })
      ),
  });
  const sellingPriceTotal = getSellingPriceTotal([installation], [], quantity);

  const SMALL_COLUMN_CLASS = 'small-column';

  return (
    <>
      <Row className="installation-row">
        <Col>
          <SelectInputFieldControlled
            disabled={offerIsLocked() || !installation.isCustomInstallation}
            id={`product-installation-${installation.key}-category`}
            name="category"
            onChange={handleFormState}
            options={getInstallationCategoryOptions(
              installationCategories,
              t,
              type,
              { ...categoryChoices, current: installation.category }
            )}
            required
            value={installation.category}
          />
        </Col>
        <Col>
          {installation.isCustomInstallation ? (
            <TextInputFieldNoLabel
              id={`product-installation-${installation.key}-installation`}
              name="customInstallation"
              onChange={handleFormState}
              value={
                installation.customInstallation === null
                  ? ''
                  : installation.customInstallation
              }
              disabled={offerIsLocked()}
            />
          ) : (
            <SearchDropdown
              className="text-field search-field"
              disabled={offerIsLocked()}
              id={`product-installation-${installation.key}-installation`}
              loadOptions={loadInstallationPrices}
              onChange={handleInstallationPriceChange}
              name="installation"
              placeholder={t(`${translations.common}:Search`)}
              required
              value={installation.installation}
            />
          )}
        </Col>
        <Col className="icon-column">
          <CollapseIcon
            collapsed={collapsed}
            onClick={() => toggleCollapse()}
            size="sm"
          />
        </Col>
        <Col>
          <NumberWithDropdownInputField
            dropdownDisabled={!installation.isCustomInstallation}
            dropdownName="quantityUnit"
            dropdownOnChange={handleFormState}
            dropdownOptions={getUnitOptions(units, t)}
            dropdownValue={installation.quantityUnit}
            id={`product-installation-${installation.key}-quantity`}
            maxIntegerDigits={MAX_QUANTITY_DIGITS}
            numberName="quantity"
            numberOnChange={handleFormState}
            numberValue={installation.quantity}
            required
            disabled={offerIsLocked()}
          />
        </Col>
        <Col>
          <NumberWithDropdownInputField
            dropdownDisabled
            dropdownName="installationPriceUnit"
            dropdownOnChange={handleFormState}
            dropdownOptions={sellingPriceUnitOptions}
            dropdownValue={installation.installationPriceUnit}
            id={`product-installation-${installation.key}-installation-price`}
            numberName="installationPrice"
            numberOnChange={handleFormState}
            numberValue={installation.installationPrice}
            required
            disabled={offerIsLocked()}
          />
        </Col>
        <Col className={SMALL_COLUMN_CLASS}>
          <NumberWithUnitInputField
            id={`product-installation-${installation.key}-social-security-cost-percentage`}
            onChange={handleFormState}
            maxIntegerDigits={MAX_SOCIAL_SECURITY_COST_PERCENTAGE_DIGITS}
            name="socialSecurityCostPercentage"
            required
            unit="%"
            value={installation.socialSecurityCostPercentage}
            disabled={offerIsLocked()}
          />
        </Col>
        <Col className={SMALL_COLUMN_CLASS}>
          <NumberWithUnitInputField
            disabled
            unit={currency}
            value={formatGroupedNumber(expenseForUnit)}
          />
        </Col>
        <Col>
          <NumberWithDropdownInputField
            dropdownDisabled
            dropdownName="sellingPriceUnit"
            dropdownOnChange={handleFormState}
            dropdownOptions={getSumUnitOptions(
              installation.key.toString(),
              currency
            )}
            dropdownValue=""
            id={`product-installation-${installation.key}-selling-price`}
            numberName="sellingPrice"
            numberOnChange={handleFormState}
            // A price per unit of the row is shown as the sum it makes, as a typed price
            numberValue={withSellingPriceAsSum(installation).sellingPrice}
            required
            disabled={offerIsLocked()}
          />
        </Col>
        {/* The selling price of the row for one unit of the position */}
        <Col className={SMALL_COLUMN_CLASS}>
          <PositionUnitPrice
            price={getSellingPricePerUnit([installation], [])}
          />
        </Col>
        <Col className={SMALL_COLUMN_CLASS}>
          <NumberWithUnitInputField
            disabled={offerIsLocked()}
            onBlur={marginInputs.eur.onBlur}
            onChange={marginInputs.eur.onChange}
            unit={currency}
            value={marginInputs.eur.value}
          />
        </Col>
        <Col className={SMALL_COLUMN_CLASS}>
          <NumberWithUnitInputField
            disabled
            unit={currency}
            value={formatGroupedNumber(expenseTotal)}
          />
        </Col>
        <Col className={SMALL_COLUMN_CLASS}>
          <NumberWithUnitInputField
            disabled
            unit={currency}
            value={formatGroupedNumber(sellingPriceTotal)}
          />
        </Col>
        <Col className={SMALL_COLUMN_CLASS}>
          <NumberWithUnitInputField
            disabled
            unit={currency}
            value={formatGroupedNumber(contributionMarginTotal)}
          />
        </Col>
        <Col className={`${SMALL_COLUMN_CLASS} ${marginRatioClassName}`}>
          <NumberWithUnitInputField
            disabled={offerIsLocked()}
            onBlur={marginInputs.percent.onBlur}
            onChange={marginInputs.percent.onChange}
            unit="%"
            value={marginInputs.percent.value}
          />
        </Col>
        <Col className="icon-column">
          {offerIsLocked() ? null : (
            <TrashIcon onClick={() => removeInstallation(installation)} />
          )}
        </Col>
      </Row>
      <InstallationDetails
        collapsed={collapsed}
        locked={locked}
        installation={installation}
        setInstallation={setInstallation}
      />
    </>
  );
};

export default Installation;
